import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, Trophy, Sparkles, X, TrendingUp, Users, Crown, Shield, Zap, Star, Award, Target, Flame, ChevronDown } from 'lucide-react';
import { fetchChallengesConfig, fetchPlayerChallengesByPuuid, fetchPlayerChallengesByRiotId, fetchAccountByPuuid } from '../services/riotFrontend';

const CARD_CLASS = 'rounded-2xl border border-white/10 bg-white/[.05] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,.25)] z-10';

const LEVEL_COLORS = {
    NONE: 'text-gray-400',
    IRON: 'text-gray-300',
    BRONZE: 'text-orange-600',
    SILVER: 'text-gray-400',
    GOLD: 'text-yellow-400',
    PLATINUM: 'text-emerald-400',
    DIAMOND: 'text-cyan-400',
    MASTER: 'text-purple-400',
    GRANDMASTER: 'text-rose-400',
    CHALLENGER: 'text-red-500'
};

const LEVEL_BG = {
    NONE: 'bg-gray-400/10 border-gray-400/30',
    IRON: 'bg-gray-300/10 border-gray-300/30',
    BRONZE: 'bg-orange-600/10 border-orange-600/30',
    SILVER: 'bg-gray-400/10 border-gray-400/30',
    GOLD: 'bg-yellow-400/10 border-yellow-400/30',
    PLATINUM: 'bg-emerald-400/10 border-emerald-400/30',
    DIAMOND: 'bg-cyan-400/10 border-cyan-400/30',
    MASTER: 'bg-purple-400/10 border-purple-400/30',
    GRANDMASTER: 'bg-rose-400/10 border-rose-400/30',
    CHALLENGER: 'bg-red-500/10 border-red-500/30'
};

// Flat bar colors for progress (no gradients)
const LEVEL_BAR = {
    NONE: 'bg-gray-500',
    IRON: 'bg-gray-400',
    BRONZE: 'bg-orange-600',
    SILVER: 'bg-gray-400',
    GOLD: 'bg-yellow-400',
    PLATINUM: 'bg-emerald-400',
    DIAMOND: 'bg-cyan-400',
    MASTER: 'bg-purple-400',
    GRANDMASTER: 'bg-rose-400',
    CHALLENGER: 'bg-red-500'
};

const CHALLENGE_ICONS = {
    101: Crown,
    102: Zap,
    103: Target,
    104: Flame,
    105: Shield,
    106: Shield,
    107: TrendingUp,
    108: Star,
    109: Award,
    // synthetic root key for ARAM Authority
    'aram_authority_root': Crown,
    // synthetic groups
    'aram_warrior': Shield,
    'aram_finesse': Flame,
    'aram_champion': Crown
};

// Preset Riot IDs for quick add
const PRESET_RIOT_IDS = [
    'Doobe#EUW',
    'Wooptido#9030',
    'Dimasre#EUW',
    'Zupa#EUW',
    'Qontroverz#EUW'
];

// LocalStorage key for persisted players (bump this if shape changes)
const STORAGE_KEY = 'aramgod_players_v1';

function PlayerSearchBar({ onAddPlayer, isLoading, presets = [] }) {
    const [query, setQuery] = useState('');
    const [showPresets, setShowPresets] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const menuRef = useRef(null);
    const btnRef = useRef(null);

    // close presets dropdown when clicking outside
    useEffect(() => {
        if (!showPresets) return;
        const onDoc = (e) => {
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            if (btnRef.current && btnRef.current.contains(e.target)) return;
            setShowPresets(false);
        };
        document.addEventListener('click', onDoc);
        return () => document.removeEventListener('click', onDoc);
    }, [showPresets]);

    // keep menu aligned on scroll/resize and close on Escape
    useEffect(() => {
        if (!showPresets) return;
        const recompute = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            const menuMin = 12 * 16; // 12rem
            const desiredWidth = Math.max(r.width, menuMin);
            const leftClamp = Math.min(Math.max(r.left, 8), Math.max(window.innerWidth - desiredWidth - 8, 8));
            const topClamp = Math.min(Math.max(r.bottom, 8), Math.max(window.innerHeight - 40, 8));
            setMenuPos({ top: topClamp, left: leftClamp, width: desiredWidth });
        };
        const onKey = (e) => { if (e.key === 'Escape') setShowPresets(false); };
        window.addEventListener('scroll', recompute, { passive: true });
        window.addEventListener('resize', recompute);
        window.addEventListener('keydown', onKey);
        recompute();
        return () => {
            window.removeEventListener('scroll', recompute);
            window.removeEventListener('resize', recompute);
            window.removeEventListener('keydown', onKey);
        };
    }, [showPresets]);

    const handleSubmit = () => {
        if (query.trim()) {
            onAddPlayer(query.trim());
            setQuery('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !isLoading && query.trim()) {
            handleSubmit();
        }
    };

    return (
        <div className="flex gap-2 items-center w-[550px]">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-cyan-200/50" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter player RiotID (name#tag), e.g. Player#EUW"
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-400/60"
                    disabled={isLoading}
                />
            </div>

            {/* Presets dropdown */}
            <div className="relative">
                <button
                    ref={btnRef}
                    onClick={() => {
                        if (!showPresets && btnRef.current) {
                            const r = btnRef.current.getBoundingClientRect();
                            const menuMin = 12 * 16;
                            const desiredWidth = Math.max(r.width, menuMin);
                            const leftClamp = Math.min(Math.max(r.left, 8), Math.max(window.innerWidth - desiredWidth - 8, 8));
                            const topClamp = Math.min(Math.max(r.bottom, 8), Math.max(window.innerHeight - 40, 8));
                            setMenuPos({ top: topClamp, left: leftClamp, width: desiredWidth });
                        }
                        setShowPresets(s => !s);
                    }}
                    className="flex gap-2 items-center justify-between px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"
                    title="Add preset"
                >
                    Presets
                    <ChevronDown className="w-4 h-4" />
                </button>

                {showPresets && presets && presets.length > 0 && menuPos && createPortal(
                    <div
                        ref={menuRef}
                        style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: '12rem', zIndex: 2147483647, transform: 'translateZ(0)' }}
                        className="bg-black/80 border border-white/10 rounded-md shadow-xl"
                    >
                        {presets.map(p => (
                            <button
                                key={p}
                                onClick={() => { setShowPresets(false); onAddPlayer(p); }}
                                className="w-full text-left px-3 py-2 text-sm text-slate-100 hover:bg-white/5"
                            >{p}</button>
                        ))}
                    </div>,
                    document.body
                )}
            </div>

            <button
                onClick={handleSubmit}
                disabled={isLoading || !query.trim()}
                className="px-4 py-2 rounded-lg bg-cyan-600/10 border border-cyan-400/30 text-cyan-200 text-sm font-medium hover:bg-cyan-600/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Add
            </button>
        </div>
    );
}

function ProgressBar({ current, max, level }) {
    const percentage = max > 0 ? Math.min(100, (current / max) * 100) : 0;

    return (
        <div className="w-full">
            <div className="h-1.5 bg-slate-800/60 rounded-full overflow-hidden">
                <div
                    className={`h-full transition-all duration-500 ${LEVEL_BAR[level || 'NONE']}`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
}

export default function AramGodComparison() {
    const [config, setConfig] = useState([]);
    const [players, setPlayers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [, setExpandedChallenges] = useState(new Set());

    const dismissError = useCallback(() => setErrorMsg(null), []);
    const hydratedRef = useRef(false);
    // Keep a ref to the latest handleAddPlayer so the hydration effect can run once on mount
    const handleAddPlayerRef = useRef(null);

    // Persist players to localStorage whenever they change — but only when non-empty.
    // When the last player is removed we explicitly remove the storage key in removePlayer to avoid writing [] on mount.
    useEffect(() => {
        if (!players || players.length === 0) return;
        try {
            const puuids = players.map(p => p?.puuid).filter(Boolean);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(puuids));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Failed to persist players to localStorage', e);
        }

         // Ensure expanded challenges include any ids present in loaded players' aramGroup.tree
         try {
             const ids = new Set();
             const collectFromNode = (node) => {
                 if (!node || !node.config) return;
                 ids.add(node.config.id);
                 (node.children || []).forEach(collectFromNode);
             };
             (players || []).forEach(p => {
                 const tree = p?.aramGroup?.tree || [];
                 if (Array.isArray(tree)) tree.forEach(collectFromNode);
             });
             if (ids.size) {
                 setExpandedChallenges(prev => {
                     const next = new Set(prev);
                     ids.forEach(i => next.add(i));
                     return next;
                 });
             }
         } catch (e) {
             // eslint-disable-next-line no-console
             console.warn('Failed to expand ARAM groups from persisted players', e);
         }
     }, [players, setExpandedChallenges]);

    const isLegacy = (c) => {
        if (!c) return false;
        const ln = c.localizedNames || {};
        const name = (ln['en-US']?.name || ln.en_US?.name || c?.name || '').toString();
        if (/:\s*(19|20)\d{2}$/.test(name)) return true;
        return /\b(19|20)\d{2}\b/.test(name) && /:\s*(19|20)\d{2}/.test(name);
    };

    useEffect(() => {
        fetchChallengesConfig()
            .then(setConfig)
            .catch((err) => {
                console.error('Failed to load challenges config', err);
                setErrorMsg(err?.message || 'Failed to load challenges configuration from backend.');
            });
    }, []);

    const aramGodChallenge = useMemo(() => config.find(c => c.isParent), [config]);

    useEffect(() => {
        if (!aramGodChallenge) return;
        setExpandedChallenges(prev => {
            if (prev.has(aramGodChallenge.id)) return prev;
            const next = new Set(prev);
            next.add(aramGodChallenge.id);
            return next;
        });
    }, [aramGodChallenge]);

    const handleAddPlayer = useCallback(async (input) => {
         const isRiotId = input.includes('#');

         if (isRiotId) {
             const already = players.some(p => (p.summonerName || '').toLowerCase() === input.toLowerCase());
             if (already) return;
         } else {
             const already = players.some(p => p.puuid === input);
             if (already) return;
         }

         setIsLoading(true);
        try {
            let data;
            if (isRiotId) {
                data = await fetchPlayerChallengesByRiotId(input);
            } else {
                data = await fetchPlayerChallengesByPuuid(input);
            }

            // If backend returned puuid as summonerName (or missing), try to resolve a human-friendly name
            try {
                const hasName = data.summonerName && data.summonerName !== data.puuid;
                if (!hasName && data.puuid) {
                    try {
                        const acct = await fetchAccountByPuuid(data.puuid);
                        const resolved = acct?.displayName || acct?.gameName || acct?.name || null;
                        if (resolved) {
                            data.summonerName = resolved;
                            // debug
                            // eslint-disable-next-line no-console
                            console.debug('AramGod: resolved summonerName via account lookup', { puuid: data.puuid, name: data.summonerName });
                        }
                    } catch (acctErr) {
                        // eslint-disable-next-line no-console
                        console.debug('AramGod: account lookup failed for puuid', data.puuid, acctErr?.message || acctErr);
                    }
                }
            } catch (e) {
                // ignore
            }

            const returnedPuuid = data?.puuid;
            setPlayers(prev => {
                if (returnedPuuid && prev.some(p => p.puuid === returnedPuuid)) {
                    return prev.map(p => p.puuid === returnedPuuid ? data : p);
                }
                return [...prev, data];
            });

            if (data?.aramGroup?.tree) {
                const collectIds = (node, set) => {
                    set.add(node.config.id);
                    (node.children || []).forEach(ch => collectIds(ch, set));
                };
                const ids = new Set();
                data.aramGroup.tree.forEach(root => collectIds(root, ids));
                setExpandedChallenges(prev => {
                    const next = new Set(prev);
                    ids.forEach(i => next.add(i));
                    return next;
                });
            }

            setErrorMsg(null);
            // Return the fetched/normalized player so callers (hydration) can dedupe by puuid.
            return data;
         } catch (error) {
            console.error('Failed to fetch player:', error);
            const hint = isRiotId ? 'Make sure the Riot ID is in format name#tag (e.g. Doobe#EUW) and public.' : 'Make sure you provided a valid PUUID.';
            setErrorMsg((error?.message ? error.message + ' ' : '') + hint);
            setTimeout(() => setErrorMsg(null), 12000);
            return null;
         } finally {
             setIsLoading(false);
         }
    }, [players]);

    const removePlayer = useCallback((puuid) => {
        setPlayers(prev => {
            const next = prev.filter(p => p.puuid !== puuid);
            try {
                if (!next.length) {
                    localStorage.removeItem(STORAGE_KEY);
                } else {
                    const puuids = next.map(p => p?.puuid).filter(Boolean);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(puuids));
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('Failed to update persisted players after removal', e);
            }
            return next;
        });
    }, []);

    const getPlayerProgress = (player, challengeId) => {
        const idStr = String(challengeId);

        const walkTree = (nodes) => {
            if (!nodes || !Array.isArray(nodes)) return null;
            for (const n of nodes) {
                const nid = String(n?.config?.id ?? n?.id ?? '');
                if (nid === idStr && n.progress) return n.progress;
                const found = walkTree(n.children || []);
                if (found) return found;
            }
            return null;
        };
        const serverTree = player?.aramGroup?.tree;
        const fromTree = walkTree(serverTree);
        if (fromTree) return fromTree;

        const list = player?.aramGroup?.playerChallenges || player?.challenges || [];
        return (list || []).find(c => String(c.challengeId ?? c.id ?? '') === idStr) || null;
    };

    const getNextThreshold = (cfg, currentValue = 0) => {
        if (!cfg) return null;
        const raw = cfg.thresholds || cfg.thresholdsByTier || null;
        if (!raw || typeof raw !== 'object') return null;

        try {
            const entries = Object.entries(raw)
                .map(([tier, val]) => ({ tier, value: Number(val) }))
                .filter(e => Number.isFinite(e.value))
                .sort((a, b) => a.value - b.value);

            const cur = Number(currentValue) || 0;
            for (const e of entries) {
                if (e.value > cur) return e;
            }
            return null;
        } catch (e) {
            console.error('getNextThreshold parse error', e);
            return null;
        }
    };

    const getHighestThresholdValue = (cfg) => {
        if (!cfg) return null;
        const raw = cfg.thresholds || cfg.thresholdsByTier || null;
        if (!raw || typeof raw !== 'object') return null;
        try {
            const entries = Object.entries(raw)
                .map(([tier, val]) => ({ tier, value: Number(val) }))
                .filter(e => Number.isFinite(e.value))
                .sort((a, b) => a.value - b.value);
            if (!entries.length) return null;
            return entries[entries.length - 1].value;
        } catch (e) {
            console.error('getHighestThresholdValue parse error', e);
            return null;
        }
    };

    // Build nested ARAM tree from config (parent -> groups -> challenges)
    const aramTree = useMemo(() => {
        if (!config || !config.length) return [];

        const containsAram = (c) => {
            if (!c) return false;
            if (isLegacy(c)) return false;
            const ln = c.localizedNames || {};
            const name = (ln['en-US']?.name || ln.en_US?.name || c?.name || '').toLowerCase();
            const desc = (ln['en-US']?.description || ln.en_US?.description || c?.shortDescription || c?.description || '').toLowerCase();
            return name.includes('aram') || desc.includes('aram');
        };

        const byParent = new Map();
        config.forEach(c => {
            const pid = c.parentId ?? null;
            if (!byParent.has(pid)) byParent.set(pid, []);
            byParent.get(pid).push(c);
        });

        const idToNode = new Map(config.map(c => [c.id, c]));
        const nodesWithAram = config.filter(containsAram);
        const rootIds = new Set();
        nodesWithAram.forEach(n => {
            let current = n;
            while (current && current.parentId != null && idToNode.has(current.parentId)) {
                current = idToNode.get(current.parentId);
            }
            if (current && !isLegacy(current)) rootIds.add(current.id);
        });

        let roots = Array.from(rootIds).map(id => idToNode.get(id));
        if (!roots.length) {
            const parent = config.find(c => c.isParent && !isLegacy(c));
            if (parent) roots = [parent];
        }

        const buildNode = (node) => {
            const rawChildren = (byParent.get(node.id) || []).filter(ch => !isLegacy(ch));
            rawChildren.sort((a, b) => {
                const aOrder = a.order ?? a.displayOrder ?? a.priority ?? null;
                const bOrder = b.order ?? b.displayOrder ?? b.priority ?? null;
                if (aOrder != null && bOrder != null && aOrder !== bOrder) return aOrder - bOrder;
                if (aOrder != null && bOrder == null) return -1;
                if (bOrder != null && aOrder == null) return 1;
                const an = (a.localizedNames?.['en-US']?.name || a.localizedNames?.en_US?.name || a.name || '').toString().toLowerCase();
                const bn = (b.localizedNames?.['en-US']?.name || b.localizedNames?.en_US?.name || b.name || '').toString().toLowerCase();
                if (an && bn && an !== bn) return an < bn ? -1 : 1;
                return (a.id || 0) - (b.id || 0);
            });
            const children = rawChildren.map(buildNode);
            return { config: node, children };
        };

        return roots.map(buildNode);
    }, [config]);

    // prune & normalize server tree if present
    const pruneTree = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return null;
        const res = [];
        for (const n of nodes) {
            const cfg = n.config || n;
            if (isLegacy(cfg)) continue;
            const childrenRaw = pruneTree(n.children || []) || [];
            childrenRaw.sort((a, b) => {
                const A = a.config || {};
                const B = b.config || {};
                const aOrder = A.order ?? A.displayOrder ?? A.priority ?? null;
                const bOrder = B.order ?? B.displayOrder ?? B.priority ?? null;
                if (aOrder != null && bOrder != null && aOrder !== bOrder) return aOrder - bOrder;
                if (aOrder != null && bOrder == null) return -1;
                if (bOrder != null && aOrder == null) return 1;
                const an = (A.localizedNames?.['en-US']?.name || A.localizedNames?.en_US?.name || A.name || '').toString().toLowerCase();
                const bn = (B.localizedNames?.['en-US']?.name || B.localizedNames?.en_US?.name || B.name || '').toString().toLowerCase();
                if (an && bn && an !== bn) return an < bn ? -1 : 1;
                return (A.id || 0) - (B.id || 0);
            });
            res.push({ config: cfg, children: childrenRaw, progress: n.progress ?? null });
        }
        return res;
    };

    const serverAramTree = useMemo(() => {
        if (!players || !players.length) return null;
        for (const p of players) {
            const tree = p?.aramGroup?.tree;
            if (tree && Array.isArray(tree)) return tree;
        }
        return null;
    }, [players]);

    const effectiveAramTree = useMemo(() => {
        if (serverAramTree) {
            const pr = pruneTree(serverAramTree);
            return pr || aramTree;
        }
        return aramTree;
    }, [serverAramTree, aramTree]);

    // Helpers for name/desc
    const nodeNameLower = (n) => {
        const ln = (n.config && n.config.localizedNames) || {};
        return (ln['en-US']?.name || ln.en_US?.name || n.config?.name || '').toString().toLowerCase();
    };
    const nodeDescLower = (n) => {
        const ln = (n.config && n.config.localizedNames) || {};
        return (ln['en-US']?.description || ln.en_US?.description || n.config?.shortDescription || n.config?.description || '').toString().toLowerCase();
    };

    // Build the final tree to render ensuring the "parent score shows next to the parent" (no duplicate child row)
    const displayAramTree = useMemo(() => {
        const tree = effectiveAramTree || [];
        if (!tree || !tree.length) return tree;

        // If server already gives one clear root with 3 groups, use it as-is
        if (tree.length === 1) {
            const nm = nodeNameLower(tree[0]);
            if (nm.includes('aram authority') || (nm.includes('aram') && (nm.includes('authority') || nm.includes('root')))) {
                const childNames = (tree[0].children || []).map(c => nodeNameLower(c));
                const hasAll = ['warrior', 'finesse', 'champion'].every(tok => childNames.some(n => n.includes(tok)));
                if (hasAll) return tree;
            }
        }

        // Bucketize nodes into warrior/finesse/champion groups
        let remaining = Array.from(tree);
        const warrior = [];
        const finesse = [];
        const champion = [];

        // Find explicit ARAM Authority node (a real challenge)
        let explicitAuthority = null;
        for (const n of remaining) {
            const nm = nodeNameLower(n);
            const ds = nodeDescLower(n);
            if (nm.includes('aram authority') || ds.includes('root aram challenge grouping')) {
                explicitAuthority = n;
                break;
            }
        }
        if (explicitAuthority) {
            remaining = remaining.filter(n => n !== explicitAuthority);
        }

        const takeIfMatch = (node) => {
            const nm = nodeNameLower(node);
            const ds = nodeDescLower(node);
            if (nm.includes('warrior') || ds.includes('warrior')) { warrior.push(node); return true; }
            if (nm.includes('finesse') || ds.includes('finesse')) { finesse.push(node); return true; }
            if (nm.includes('champion') || ds.includes('champion')) { champion.push(node); return true; }
            return false;
        };
        remaining = remaining.filter(n => !takeIfMatch(n));

        remaining = remaining.filter(n => {
            const ch = n.children || [];
            if (!ch.length) return true;
            const chNames = ch.map(c => nodeNameLower(c));
            if (chNames.some(x => x.includes('warrior'))) { warrior.push(n); return false; }
            if (chNames.some(x => x.includes('finesse'))) { finesse.push(n); return false; }
            if (chNames.some(x => x.includes('champion'))) { champion.push(n); return false; }
            return true;
        });

        const buckets = [warrior, finesse, champion];
        let idx = 0;
        for (const n of remaining) {
            buckets[idx % 3].push(n);
            idx++;
        }

        const makeGroup = (id, title, childrenArr, token) => {
            if (childrenArr && childrenArr.length === 1) {
                const single = childrenArr[0];
                const nm = (single.config?.localizedNames?.['en-US']?.name || single.config?.localizedNames?.en_US?.name || single.config?.name || '').toString().toLowerCase();
                if (nm.includes(token)) return single;
            }
            return { config: { id, name: title, localizedNames: { 'en-US': { name: title } } }, children: childrenArr, progress: null };
        };

        const groups = [
            makeGroup('aram_warrior', 'ARAM Warrior', warrior, 'warrior'),
            makeGroup('aram_finesse', 'ARAM Finesse', finesse, 'finesse'),
            makeGroup('aram_champion', 'ARAM Champion', champion, 'champion')
        ];

        // ✅ If we have a real "ARAM Authority" challenge, use it as the root so its score appears on the parent row.
        if (explicitAuthority) {
            // If explicit already has 3 groups as children, keep them; otherwise replace children with our computed groups.
            const childNames = (explicitAuthority.children || []).map(c => nodeNameLower(c));
            const hasAll = ['warrior', 'finesse', 'champion'].every(tok => childNames.some(n => n.includes(tok)));
            const rootChildren = hasAll ? explicitAuthority.children : groups;

            return [{
                config: explicitAuthority.config,
                children: rootChildren,
                progress: explicitAuthority.progress ?? null
            }];
        }

        // Otherwise, use synthetic wrapper WITHOUT progress (wrapper only)
        const syntheticRoot = {
            config: {
                id: 'aram_authority_root',
                name: 'ARAM Authority',
                localizedNames: { 'en-US': { name: 'ARAM Authority', description: 'Root ARAM challenge grouping' } }
            },
            children: groups,
            progress: null
        };

        return [syntheticRoot];
    }, [effectiveAramTree]);

    // Helper to drop duplicate "child challenge equals its parent" rows
    const isDuplicateChildOfParent = (child, parent) => {
        const childId = String(child?.config?.id ?? '');
        const parentId = String(parent?.config?.id ?? '');
        if (childId && parentId && childId === parentId) return true;
        const cn = nodeNameLower(child);
        const pn = nodeNameLower(parent);
        // If child is a leaf with the exact same name as the parent, treat as duplicate
        const leaf = !child?.children || child.children.length === 0;
        return leaf && cn && pn && cn === pn;
    };

    const renderTreeRows = (nodes, depth = 0) => {
        const rows = [];
        if (!nodes || !nodes.length) return rows;

        nodes.forEach((node) => {
            const id = node.config?.id;
            const Icon = CHALLENGE_ICONS[id] || Trophy;
            const ln = node.config?.localizedNames || {};

            // parent/group row
            if (node.children && node.children.length) {
                const name = ln['en-US']?.name || ln.en_US?.name || node.config?.name || `#${id}`;
                const desc = ln['en-US']?.description || ln.en_US?.description || node.config?.shortDescription || node.config?.description || '';

                rows.push(
                    <tr key={`group-${id}`} className="bg-black/10">
                        <td className="p-4 sticky left-0 bg-black/20 backdrop-blur-md">
                            <div className="flex items-center gap-3">
                                <Icon className="w-4 h-4 text-cyan-300" />
                                <div>
                                    <div className="text-sm font-semibold text-cyan-300">{name}</div>
                                    {desc && <div className="text-xs text-slate-500">{desc}</div>}
                                </div>
                            </div>
                        </td>

                        {players.map((player) => {
                            // ✅ Show parent progress inline (so the parent has its score next to it)
                            const progress = getPlayerProgress(player, id);
                            if (!progress) {
                                return (
                                    <td key={`${player.puuid}-group-${id}`} className="p-4">
                                        <div className="text-xs text-slate-500">—</div>
                                    </td>
                                );
                            }

                            const cur = Number(progress?.points ?? progress?.pointsEarned ?? progress?.pointsAwarded ?? progress?.pointsCurrent ?? progress?.value ?? 0);
                            const next = getNextThreshold(node.config, cur);
                            const fallbackMax = getHighestThresholdValue(node.config);
                            const max = next?.value ?? fallbackMax ?? null;
                            const displayMax = max != null ? Number(max) : null;
                            const currentDisplay = cur.toLocaleString();
                            const maxDisplay = displayMax != null ? displayMax.toLocaleString() : '—';

                            return (
                                <td key={`${player.puuid}-group-${id}`} className="p-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${LEVEL_BG[progress?.level || 'NONE']} ${LEVEL_COLORS[progress?.level || 'NONE']}`}>
                                                {progress?.level || 'NONE'}
                                            </span>
                                            <span className="text-xs text-slate-400">{`${currentDisplay}/${maxDisplay}`}</span>
                                        </div>
                                        {displayMax && <ProgressBar current={cur} max={displayMax} level={progress?.level || 'NONE'} />}
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                );

                // ✅ Render children, but drop any child that's effectively the same "parent challenge"
                const childrenToRender = (node.children || []).filter(ch => !isDuplicateChildOfParent(ch, node));
                rows.push(...renderTreeRows(childrenToRender, depth + 1));
                return;
            }

            // regular challenge row
            const indentClass = depth === 0 ? '' : depth === 1 ? 'pl-8' : 'pl-12';
            rows.push(
                <tr key={id} className="hover:bg-white/[0.02] transition">
                    <td className={`p-4 ${indentClass} sticky left-0 bg-black/20 backdrop-blur-md`}>
                        <div className="flex items-center gap-3">
                            <Icon className="w-4 h-4 text-slate-400" />
                            <div>
                                <div className="text-sm text-slate-200">{ln['en-US']?.name || ln.en_US?.name || node.config?.name || `#${id}`}</div>
                                <div className="text-xs text-slate-500">{ln['en-US']?.description || ln.en_US?.description || node.config?.shortDescription || node.config?.description || ''}</div>
                            </div>
                        </div>
                    </td>
                    {players.map((player) => {
                        const progress = getPlayerProgress(player, id);
                        if (!progress) return (<td key={`${player.puuid}-${id}`} className="p-4"><div className="text-xs text-slate-500">—</div></td>);

                        const cur = Number(progress?.points ?? progress?.pointsEarned ?? progress?.pointsAwarded ?? progress?.pointsCurrent ?? progress?.value ?? 0);
                        const next = getNextThreshold(node.config, cur);
                        const fallbackMax = getHighestThresholdValue(node.config);
                        const max = next?.value ?? fallbackMax ?? null;
                        const displayMax = max != null ? Number(max) : null;
                        const currentDisplay = cur.toLocaleString();
                        const maxDisplay = displayMax != null ? displayMax.toLocaleString() : '—';

                        return (
                            <td key={`${player.puuid}-${id}`} className="p-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${LEVEL_BG[progress?.level || 'NONE']} ${LEVEL_COLORS[progress?.level || 'NONE']}`}>
                                            {progress?.level || 'NONE'}
                                        </span>
                                        <span className="text-xs text-slate-400">{`${currentDisplay}/${maxDisplay}`}</span>
                                    </div>
                                    {displayMax && <ProgressBar current={cur} max={displayMax} level={progress?.level || 'NONE'} />}
                                </div>
                            </td>
                        );
                    })}
                </tr>
            );
        });
        return rows;
    };

    // Keep a ref to the latest handleAddPlayer so the hydration effect can call it without being a dependency
    useEffect(() => { handleAddPlayerRef.current = handleAddPlayer; }, [handleAddPlayer]);

    // We'll hydrate once on mount using the ref to avoid re-running when handleAddPlayer identity changes.
    useEffect(() => {
        if (hydratedRef.current) return;
        let cancelled = false;
        (async () => {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                if (!raw) {
                    hydratedRef.current = true;
                    // debug
                    // eslint-disable-next-line no-console
                    console.debug('AramGod: no persisted storage key found, marking hydrated');
                    return;
                }
                const parsed = JSON.parse(raw);
                const entries = Array.isArray(parsed) ? parsed : [];
                const normalized = entries.map(e => {
                    if (typeof e === 'string') return e;
                    if (e && typeof e === 'object') {
                        if (e.puuid) return e.puuid;
                        if (e.summonerName) return e.summonerName;
                    }
                    return null;
                }).filter(Boolean);

                if (!normalized.length) {
                    hydratedRef.current = true;
                    // debug
                    // eslint-disable-next-line no-console
                    console.debug('AramGod: no persisted entries found, marking hydrated');
                    return;
                }

                // debug
                // eslint-disable-next-line no-console
                console.debug('AramGod: hydrating via handleAddPlayer', { raw, parsed, normalized });

                setIsLoading(true);
                const seenInputs = new Set();
                const seenPuuids = new Set();
                for (const value of normalized) {
                    if (cancelled) break;
                    const key = String(value).trim();
                    if (!key || seenInputs.has(key)) continue;
                    seenInputs.add(key);
                    // eslint-disable-next-line no-await-in-loop
                    const result = await handleAddPlayerRef.current?.(key);
                    const puuid = result?.puuid;
                    if (puuid) {
                        if (seenPuuids.has(puuid)) {
                            // already added by previous input mapping to same puuid
                            continue;
                        }
                        seenPuuids.add(puuid);
                    }
                }
                 hydratedRef.current = true;
             } catch (err) {
                 // eslint-disable-next-line no-console
                 console.warn('AramGod: hydration failed', err);
                 hydratedRef.current = true;
             } finally {
                 if (!cancelled) setIsLoading(false);
             }
         })();
         return () => { cancelled = true; };
     }, []);

    // Render
    return (
        <div className="min-h-screen bg-[#060b16]">
            <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
                {/* Header */}
                <header className="text-center space-y-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-200 text-xs uppercase tracking-[0.2em]">
                        <Crown className="w-4 h-4" />
                        ARAM God Comparison
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-white drop-shadow-lg">Challenge Progress Dashboard</h1>
                </header>

                {/* Error banner */}
                {errorMsg && (
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="rounded-lg border border-red-500/20 bg-red-900/30 text-sm text-red-100 p-3 flex items-start gap-3">
                            <Shield className="w-5 h-5 text-red-300 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-medium">Could not fetch player data</div>
                                <div className="text-xs text-red-200 mt-2">Quick checks: ensure the backend is running (default: <code>http://localhost:3001</code>), has RIOT_API_KEY set, and CORS is enabled.</div>
                            </div>
                            <button onClick={dismissError} className="p-1 rounded hover:bg-white/5">
                                <X className="w-4 h-4 text-red-200" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Controls Bar */}
                <div className={`${CARD_CLASS} p-4 flex items-center justify-between gap-4`}>
                    <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-cyan-300" />
                        <span className="text-sm text-slate-300">{players.length} {players.length === 1 ? 'Player' : 'Players'} Added</span>
                    </div>
                    <PlayerSearchBar onAddPlayer={handleAddPlayer} isLoading={isLoading} presets={PRESET_RIOT_IDS} />
                </div>

                {/* Comparison Table */}
                {players.length > 0 && (
                    <div className={`${CARD_CLASS} overflow-hidden`}>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="border-b border-white/10">
                                    <tr>
                                        <th className="text-left p-4 text-xs uppercase tracking-wider text-slate-400 font-medium sticky left-0 bg-black/20 backdrop-blur-md">Challenge</th>
                                        {players.map(player => (
                                            <th key={player.puuid || player.summonerName} className="p-4 text-center min-w-[180px]">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-sm font-semibold text-cyan-200">{player.summonerName}</span>
                                                    <button onClick={() => removePlayer(player.puuid)} className="p-1 rounded hover:bg-white/10 transition">
                                                        <X className="w-3 h-3 text-slate-400" />
                                                    </button>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">{renderTreeRows(displayAramTree)}</tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {players.length === 0 && (
                    <div className={`${CARD_CLASS} p-16 text-center`}>
                        <Trophy className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-300 mb-2">No Players Added</h3>
                        <p className="text-sm text-slate-500 mb-6">Start by searching for a summoner to compare ARAM challenge progress</p>
                    </div>
                )}

                {/* Stats Summary */}
                {players.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {players.map(player => (
                            <div key={player.puuid || player.summonerName} className={`${CARD_CLASS} p-6`}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-sm font-semibold text-cyan-200">{player.summonerName}</h3>
                                    <Sparkles className="w-4 h-4 text-cyan-300" />
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-slate-400 uppercase tracking-wide">Total Points</p>
                                        <p className="text-xl font-bold text-white">{player.totalPoints?.current?.toLocaleString() || 0}</p>
                                    </div>
                                    <div className="pt-2 border-t border-white/10">
                                        <p className="text-xs text-slate-500">Last updated: {new Date(player.updatedAt).toLocaleTimeString()}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

